import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useAudioEngine } from "./hooks/useAudioEngine";
import { DEFAULT_PARAMS } from "./constants/presets";
import { cn } from "../../utils/cn";

// Components
import MicPanel from "./components/MicPanel";
import VisualizerPanel from "./components/VisualizerPanel";
import {
  EchoDelayModule,
  EnvironmentPanModule,
  AcousticFiltersEQModule,
  AcousticPresetConsole,
} from "./components/ControlPanel";
import RecordControls from "./components/RecordControls";
import BackingTrackPlayer from "./components/BackingTrackPlayer";

const VoiceEchoStudio = () => {
  const {
    isSupported,
    micState,
    errorMessage,
    params,
    levels,
    isRecording,
    recordResult,
    musicLoaded,
    musicPlaying,
    musicName,
    musicProgress,
    startMic,
    stopMic,
    updateParam,
    loadPreset,
    startRecording,
    stopRecording,
    clearRecordResult,
    getAnalyserData,
    loadMusicTrack,
    playMusicTrack,
    pauseMusicTrack,
    seekMusicTrack,
  } = useAudioEngine();

  const handleReset = () => {
    // Reset to default parameters
    Object.entries(DEFAULT_PARAMS).forEach(([key, val]) => {
      updateParam(key, val);
    });
    // Clear old compiled recording track
    clearRecordResult();
  };

  return (
    <>
      <Helmet>
        <title>Voice Echo Studio - DeveloperNoob</title>
        <meta name="description" content="Live Real-Time Voice Effects Processor and Echo Studio" />
        <meta
          name="keywords"
          content="audio effects, live voice, echo studio, web audio api, soundboard, delay node, reverb"
        />
      </Helmet>

      <div className="min-h-screen lg:h-screen lg:max-h-screen flex flex-col bg-background text-foreground relative overflow-x-hidden lg:overflow-hidden theme-transition">
        {/* Scan lines overlay for terminal aesthetic */}
        <div className="scan-lines fixed inset-0 pointer-events-none z-10"></div>

        {/* Global Page Header */}
        <Header />

        <main className="flex-1 pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full flex flex-col overflow-y-auto pb-24 lg:pb-4 lg:overflow-hidden lg:h-[calc(100vh-80px)]">
          {/* Header text */}
          <div className="text-center mb-4 font-fira flex-shrink-0">
            <div className="flex items-center justify-center space-x-3 mb-1">
              <div className="p-1.5 bg-primary/10 border border-primary/20 rounded-md terminal-glow">
                <Icon name="Volume2" size={24} className="text-primary animate-pulse" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold uppercase tracking-wider text-foreground">
                Voice Echo Studio
              </h1>
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground font-source max-w-xl mx-auto">
              Real-time Web Audio FX Console. Apply digital loops, space acoustics,
              and low-latency commentary filters directly in your browser.
            </p>
          </div>

          {!isSupported ? (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-2xl mx-auto text-center font-fira my-12">
              <Icon name="AlertOctagon" size={48} className="text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-bold text-destructive mb-2 uppercase">Browser Not Supported</h3>
              <p className="text-xs text-muted-foreground leading-relaxed font-source">
                Your browser does not support the Web Audio API or Microphone media capture APIs. 
                Please use a modern browser like Google Chrome, Microsoft Edge, Safari, or Mozilla Firefox.
              </p>
            </div>
          ) : (
            <div className="flex-grow flex flex-col lg:grid lg:grid-cols-12 gap-6 items-stretch w-full lg:h-[calc(100vh-140px)] lg:overflow-hidden">
              
              {/* Column 1: Vocal Input Console (Left on Desktop, 1st Stack on Mobile) */}
              <div className="w-full lg:col-span-3 flex-shrink-0 flex flex-col lg:h-full lg:overflow-y-auto pr-1 scrollbar-thin order-1 lg:order-none">
                <MicPanel
                  micState={micState}
                  errorMessage={errorMessage}
                  inputLevel={levels.inputLevel}
                  outputLevel={levels.outputLevel}
                  onStart={startMic}
                  onStop={stopMic}
                />
              </div>

              {/* Column 2: Monitoring, Tracks, & Presets (Middle on Desktop - Large, 2nd Stack on Mobile) */}
              <div className="w-full lg:col-span-5 flex-shrink-0 flex flex-col gap-4 lg:h-full lg:overflow-y-auto pr-1 scrollbar-thin order-2 lg:order-none">
                {/* Visualizer CRT monitor */}
                <div className="h-[250px] lg:h-[220px] flex-shrink-0">
                  <VisualizerPanel
                    micState={micState}
                    isMuted={params.isMuted}
                    getAnalyserData={getAnalyserData}
                    outputLevel={levels.outputLevel}
                  />
                </div>

                {/* Backing Track Player */}
                <div className="flex-shrink-0">
                  <BackingTrackPlayer
                    musicLoaded={musicLoaded}
                    musicPlaying={musicPlaying}
                    musicName={musicName}
                    musicProgress={musicProgress}
                    musicVolume={params.musicVolume}
                    onLoadTrack={loadMusicTrack}
                    onPlay={playMusicTrack}
                    onPause={pauseMusicTrack}
                    onSeek={seekMusicTrack}
                    onUpdateVolume={(val) => updateParam("musicVolume", val)}
                  />
                </div>

                {/* Cassette Recording deck */}
                <div className="flex-shrink-0">
                  <RecordControls
                    isRecording={isRecording}
                    recordResult={recordResult}
                    isMuted={params.isMuted}
                    micConnected={micState === "connected"}
                    onToggleMute={() => updateParam("isMuted", !params.isMuted)}
                    onStartRecord={startRecording}
                    onStopRecord={stopRecording}
                    onResetParams={handleReset}
                  />
                </div>

                {/* Acoustic Preset Console */}
                <div className="flex-shrink-0">
                  <AcousticPresetConsole
                    params={params}
                    onLoadPreset={loadPreset}
                    disabled={micState !== "connected"}
                  />
                </div>
              </div>

              {/* Column 3: Processing & Sliders (Right on Desktop, 3rd Stack on Mobile) */}
              <div className="w-full lg:col-span-4 flex-shrink-0 flex flex-col gap-4 lg:h-full lg:overflow-y-auto pr-1 scrollbar-thin order-3 lg:order-none">
                <EchoDelayModule
                  params={params}
                  onUpdateParam={updateParam}
                  disabled={micState !== "connected"}
                />

                <EnvironmentPanModule
                  params={params}
                  onUpdateParam={updateParam}
                  disabled={micState !== "connected"}
                />

                <AcousticFiltersEQModule
                  params={params}
                  onUpdateParam={updateParam}
                  disabled={micState !== "connected"}
                />
              </div>

            </div>
          )}
        </main>
        
        {/* Render Footer only on mobile since desktop fits exactly 100vh */}
        <div className="lg:hidden">
          <Footer />
        </div>

        {/* Floating Sticky Device Control Bar on Mobile */}
        <div className="lg:hidden fixed bottom-4 left-4 right-4 z-50">
          {micState !== "connected" ? (
            <button
              onClick={startMic}
              disabled={micState === "connecting"}
              className={cn(
                "w-full py-3 px-6 rounded-lg font-bold tracking-[0.15em] text-[10px] flex items-center justify-center space-x-2 border transition-all duration-200 select-none shadow-[0_4px_20px_rgba(16,185,129,0.35),inset_0_1px_1px_rgba(255,255,255,0.1)] active:translate-y-0.5 active:shadow-inner cursor-pointer",
                micState === "connecting"
                  ? "bg-[#eab308]/90 border-[#eab308] text-black cursor-wait animate-pulse"
                  : "bg-gradient-to-b from-[#10b981] to-[#047857] border-[#10b981] text-black hover:shadow-[0_0_20px_rgba(16,185,129,0.5)] animate-pulse"
              )}
              style={{ animationDuration: "2.5s" }}
            >
              {micState === "connecting" ? (
                <>
                  <Icon name="Loader" size={14} className="animate-spin" />
                  <span>ENGAGING VOICE CONSOLE...</span>
                </>
              ) : (
                <>
                  <Icon name="Power" size={14} />
                  <span>START VOCAL CAPTURE</span>
                </>
              )}
            </button>
          ) : (
            <div className="bg-[#11161d]/95 backdrop-blur-md border border-[#10b981]/30 p-3 rounded-lg shadow-[0_4px_24px_rgba(0,255,65,0.25)] flex items-center justify-between font-fira terminal-glow-strong">
              <div className="flex items-center space-x-2">
                <div className="w-2.5 h-2.5 rounded-full bg-success shadow-[0_0_8px_var(--color-primary)] animate-pulse" />
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider">LIVE STUDIO</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant={params.isMuted ? "danger" : "outline"}
                  size="xs"
                  onClick={() => updateParam("isMuted", !params.isMuted)}
                  iconName={params.isMuted ? "VolumeX" : "Volume2"}
                  className="h-8 px-2.5 text-[10px] font-bold uppercase"
                >
                  {params.isMuted ? "MUTED" : "MUTE"}
                </Button>
                <Button
                  variant="destructive"
                  size="xs"
                  onClick={stopMic}
                  iconName="MicOff"
                  className="h-8 px-2.5 text-[10px] font-bold uppercase"
                >
                  RELEASE
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VoiceEchoStudio;
